/**
 * エミュレータ層の型定義。
 *
 * 入力は libretro の RetroPad ビットマスクに合わせてある。こうしておくと
 * RetroArch 系 WASM コア (fceumm / snes9x / gambatte / pcsx-rearmed 等) を
 * 追加するときに変換レイヤが要らない。
 */

export type SystemId = 'gb' | 'gba' | 'nes' | 'snes' | 'psx' | 'ws' | 'psp';

export interface SystemInfo {
  id: SystemId;
  /** 画面表示名 */
  name: string;
  /** 短縮ラベル（バッジ等） */
  short: string;
  /** ネイティブ解像度（バーチャルパッドのレイアウト計算にも使う） */
  nativeWidth: number;
  nativeHeight: number;
  /** 表示アスペクト比 (w/h)。SNES/NES は非正方ピクセル。 */
  aspect: number;
  /** 標準フレームレート */
  fps: number;
  /** 対応する拡張子（小文字・ドット無し） */
  extensions: string[];
  /** 起動に BIOS が要るか（PS1 = true） */
  requiresBios: boolean;
  /**
   * BIOS を受け付けるか。
   * requiresBios が false でも true になりうる（GBA は HLE で動くが、
   * 実機 BIOS を入れると起動ロゴなど一部の挙動がより正確になる）。
   */
  acceptsBios: boolean;
  /** この機種で意味を持つボタン（バーチャルパッドの出し分けに使う） */
  buttons: PadButton[];
}

/* -------------------------------------------------------------------------- */
/* 入力                                                                        */
/* -------------------------------------------------------------------------- */

/** RetroPad 準拠のビット位置 */
export const PAD = {
  B: 0,
  Y: 1,
  SELECT: 2,
  START: 3,
  UP: 4,
  DOWN: 5,
  LEFT: 6,
  RIGHT: 7,
  A: 8,
  X: 9,
  L: 10,
  R: 11,
  L2: 12,
  R2: 13,
  L3: 14,
  R3: 15,
} as const;

export type PadButton = keyof typeof PAD;

export const PAD_BUTTONS = Object.keys(PAD) as PadButton[];

export const bit = (b: PadButton): number => 1 << PAD[b];

/** アナログスティック（PS1 用）。-1.0 .. 1.0 */
export interface AnalogState {
  leftX: number;
  leftY: number;
  rightX: number;
  rightY: number;
}

export interface InputSnapshot {
  /** プレイヤー毎のボタンビットマスク（index 0 = P1） */
  buttons: Uint16Array;
  analog: AnalogState;
}

/* -------------------------------------------------------------------------- */
/* 映像 / 音声                                                                 */
/* -------------------------------------------------------------------------- */

export type PixelFormat = 'RGB565' | 'XRGB8888' | 'RGBA8888';

export interface VideoFrame {
  /** ピクセルデータ。format に応じて Uint16Array / Uint32Array。 */
  data: Uint16Array | Uint32Array | Uint8ClampedArray;
  width: number;
  height: number;
  /** 行あたりのバイト数（コアがパディングする場合がある） */
  pitch: number;
  format: PixelFormat;
}

export interface AudioChunk {
  /** インターリーブされたステレオ (L,R,L,R...) の 16bit PCM */
  samples: Int16Array;
  sampleRate: number;
}

/* -------------------------------------------------------------------------- */
/* コア（フレーム単位で駆動できる低レベルコア）                                 */
/* -------------------------------------------------------------------------- */

export type RomBytes = Uint8Array | Blob;

export interface CoreLoadOptions {
  system: SystemId;
  romName: string;
  rom: RomBytes;
  /**
   * CUE+BIN など主ファイル以外のディスク構成ファイル。
   * key は basename（CUE 内の FILE 名と一致させる）。
   * Blob なら起動時に 1 ファイルずつ展開してメモリ圧を下げる。
   */
  extraFiles?: Record<string, RomBytes> | undefined;
  /** PS1 等で必要な BIOS */
  bios?: Uint8Array | undefined;
  /** BIOS を FS に置くファイル名（例: scph5500.bin）。省略時はコア既定名 */
  biosFilename?: string | undefined;
  /** 前回のセーブ（バッテリーバックアップ SRAM） */
  sram?: Uint8Array | undefined;
  /**
   * 読み込み進捗（0–100）。メインスレッドでも Worker 経由でも使える。
   * Worker では関数を渡せないので、バックエンドが postMessage に橋渡しする。
   */
  onProgress?: ((p: LoadProgress) => void) | undefined;
}

/** 起動パイプラインの進捗。UI の％表示に使う。 */
export interface LoadProgress {
  /** 0–100 */
  percent: number;
  /** 「コアを準備中」「ディスクを展開中」など */
  label: string;
}

export interface CoreAvSpec {
  width: number;
  height: number;
  fps: number;
  sampleRate: number;
  pixelFormat: PixelFormat;
}

/** マルチディスクの現在状態。 */
export interface DiscInfo {
  count: number;
  index: number;
  labels: string[];
}

/**
 * こちらがフレームループを回す種類のコア。
 * 自前実装コアや、フレーム単位 API を持つ WASM コアはこれを実装する。
 */
export interface FrameCore {
  readonly id: string;
  readonly systems: readonly SystemId[];

  init(): Promise<void>;
  load(opts: CoreLoadOptions): Promise<CoreAvSpec>;

  /** 1 フレーム進める。返り値はそのフレームの映像。 */
  runFrame(input: InputSnapshot): VideoFrame;
  /** 直前の runFrame で生成された音声を取り出す。 */
  drainAudio(): AudioChunk | null;

  reset(): void;
  saveState(): Uint8Array;
  loadState(state: Uint8Array): void;
  /** バッテリーバックアップ。無いカートリッジは null。 */
  getSram(): Uint8Array | null;
  /**
   * SAVE_RAM をコアへ書き戻す。
   * PS1 メモリーカード修復後にライブへ再注入する用途。非対応は false。
   */
  setSram?(data: Uint8Array): boolean;

  /**
   * チートコードを適用する。既存の設定は毎回置き換える。
   * 対応しないコアは実装しなくてよい（呼び出し側で存在を確認する）。
   */
  setCheats?(codes: string[]): void;

  /**
   * マルチディスク情報。未対応や単一ディスクなら count <= 1。
   */
  getDiscInfo?(): DiscInfo;

  /**
   * ディスクを切り替える（イジェクト → インデックス変更 → 挿入）。
   * 非対応なら実装しない。
   */
  swapDisc?(index: number): void;

  dispose(): void;
}

/* -------------------------------------------------------------------------- */
/* バックエンド（UI から見た統一インタフェース）                                */
/* -------------------------------------------------------------------------- */

export interface RewindStatus {
  /** 機能として使えるか（コアがステートに対応しているか） */
  available: boolean;
  /** 現在巻き戻し中か */
  active: boolean;
  /** 遡れる秒数の目安 */
  seconds: number;
  /** 使えない場合の理由 */
  reason?: string | undefined;
}

export interface BackendMountOptions {
  /** 描画先。バックエンドが自前で canvas を作る場合の親要素。 */
  container: HTMLElement;
  /** 音量 0.0 - 1.0 */
  volume: number;
  /** 入力を毎フレーム取りにいくためのコールバック */
  pollInput: () => InputSnapshot;
  /** 縦方向を整数倍に丸める */
  integerScale?: boolean;
  /** 表示フィルタ。'none' 以外を指定すると WebGL で描画する。 */
  shader?: import('./video/shaders').ShaderId;
  onError: (err: Error) => void;
  onFps?: (fps: number) => void;
  /** 巻き戻しが使えないと判明したときの通知 */
  onRewindUnavailable?: (reason: string) => void;
}

/**
 * UI が触るのはこの層だけ。
 * 「自分でフレームを回すコア」も「RetroArch のように自走するコア」も、
 * この形に揃えることで差し替え可能にする。
 */
export interface EmulatorBackend {
  readonly id: string;
  readonly systems: readonly SystemId[];
  readonly displayName: string;

  mount(opts: BackendMountOptions): Promise<void>;
  load(opts: CoreLoadOptions): Promise<void>;

  start(): void;
  pause(): void;
  resume(): void;
  reset(): void;
  get running(): boolean;

  setVolume(v: number): void;
  /** 倍速。1 = 等速、0.5 = スロー、2 = 倍速 */
  setSpeed(mult: number): void;

  /** チートコードを適用する。非対応コアでは false を返す。 */
  setCheats(codes: string[]): boolean;

  /** マルチディスク情報。非対応なら null。 */
  getDiscInfo(): Promise<DiscInfo | null>;

  /** ディスクを切り替える。非対応なら false。 */
  swapDisc(index: number): Promise<boolean>;

  /** 表示フィルタを切り替える。WebGL 非対応なら false。 */
  setShader(id: import('./video/shaders').ShaderId): boolean;

  /** 巻き戻しの有効化。コアがステート非対応なら黙って無視される。 */
  setRewindEnabled(on: boolean): void;
  /** 巻き戻し中かどうかを切り替える（ボタンを押している間 true） */
  setRewinding(on: boolean): void;
  get rewindStatus(): RewindStatus;

  saveState(): Promise<Uint8Array>;
  loadState(state: Uint8Array): Promise<void>;
  getSram(): Promise<Uint8Array | null>;
  /** SAVE_RAM をコアへ書き戻す。非対応なら false。 */
  setSram(data: Uint8Array): Promise<boolean>;
  screenshot(): Promise<Blob | null>;

  dispose(): void;
}

export type BackendFactory = () => EmulatorBackend;

export interface BackendRegistration {
  id: string;
  displayName: string;
  systems: readonly SystemId[];
  /** 大きい方が優先される */
  priority: number;
  /** 必要なアセットが配置済みかを実行時に確認する */
  isAvailable: () => Promise<boolean>;
  create: BackendFactory;
  /** Worker 内で同じコアを組み立てるための指定。無ければ Worker 実行に対応しない。 */
  coreSpec?: import('./worker/protocol').CoreSpec;
}
